import type { FC, ReactNode } from 'react'

import { ArrowRight, BookOpen } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import SiteHeader from '@/components/landing/site-header'
import SiteFooter from '@/components/landing/site-footer'
import { GUIDES } from '@/constants/guides'

const GuidesPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title='Guides' description='Short, practical guides on image upload limits, dimensions, file formats, photo metadata and finishing a PDF.' />

            <div className='min-h-screen bg-[#060b14] text-slate-200'>
                <SiteHeader />

                <section id='hero' className='relative overflow-hidden'>
                    <div className='pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[900px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-3xl' />
                    <div className='relative mx-auto max-w-6xl px-6 pt-20 pb-12 md:pt-28'>
                        <p className='font-mono text-xs tracking-[0.2em] text-cyan-300 uppercase'>Practical file guides</p>
                        <h1 className='mt-5 max-w-4xl text-4xl leading-[1.05] font-extrabold tracking-tight text-white md:text-6xl'>
                            A few settings. <span className='font-serif-accent font-normal text-amber-300'>A lot less guesswork.</span>
                        </h1>
                        <p className='mt-6 max-w-2xl text-lg leading-relaxed text-slate-400'>
                            Short guides for upload limits, image dimensions, file formats, metadata and reviewing a finished PDF. Read what you need, then try the tool.
                        </p>
                    </div>
                </section>

                <section id='guides' className='mx-auto max-w-6xl px-6 pb-20'>
                    <div className='grid gap-4 md:grid-cols-2'>
                        {GUIDES.map((guide) => (
                            <a key={guide.slug} href={`/${guide.slug}`} className='group flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition hover:border-cyan-300/40 hover:bg-white/[0.04] sm:p-8'>
                                <span className='inline-flex items-center gap-2 text-xs font-medium text-slate-500'>
                                    <BookOpen className='size-4 text-cyan-300' /> Guide
                                </span>
                                <h2 className='mt-4 text-xl font-bold text-white'>{guide.title}</h2>
                                <p className='mt-2 text-sm leading-relaxed text-slate-400'>{guide.summary}</p>
                                <span className='mt-auto inline-flex items-center gap-2 pt-6 text-sm font-semibold text-cyan-300'>
                                    Read guide <ArrowRight className='size-4 transition group-hover:translate-x-0.5' />
                                </span>
                            </a>
                        ))}
                    </div>
                </section>

                <section id='why' className='border-y border-white/10 bg-[#081120]'>
                    <div className='mx-auto grid max-w-6xl gap-10 px-6 py-20 md:grid-cols-2'>
                        <div>
                            <h2 className='text-2xl font-extrabold text-white'>Why the guides are here</h2>
                            <p className='mt-4 leading-relaxed text-slate-400'>
                                Each guide explains the tradeoff behind a tool setting, and what a tool cannot do. Converting a JPG to PNG, for example, will not recover detail already lost to compression. The tools do the work. The guides help you make the choice.
                            </p>
                        </div>
                        <div className='space-y-4'>
                            <a href='/tools' className='flex items-center justify-between rounded-2xl border border-white/10 p-5 transition hover:border-cyan-300/40'>
                                <span>
                                    <span className='block font-bold text-white'>Free tools</span>
                                    <span className='text-sm text-slate-400'>Quick browser utilities for images and PDFs.</span>
                                </span>
                                <ArrowRight className='size-5 shrink-0 text-cyan-300' />
                            </a>
                            <a href='/support' className='flex items-center justify-between rounded-2xl border border-white/10 p-5 transition hover:border-cyan-300/40'>
                                <span>
                                    <span className='block font-bold text-white'>Need direct help?</span>
                                    <span className='text-sm text-slate-400'>DOCSCRUB billing, saving and troubleshooting questions.</span>
                                </span>
                                <ArrowRight className='size-5 shrink-0 text-cyan-300' />
                            </a>
                        </div>
                    </div>
                </section>

                <SiteFooter />
            </div>
        </Layout>
    )
}

export default GuidesPage
